/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";

// PrimeNG imports
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";

// Our interfaces
import { PaymentMethod } from "../../interfaces/customer-payment-method.interface";

/**
 * Customer Payment Methods Section Component
 * Displays payment methods with actions (set default, delete)
 */
@Component({
  selector: "app-customer-payment-methods-section",
  standalone: true,
  imports: [CommonModule, CardModule, ButtonModule, TagModule, TooltipModule],
  templateUrl: "./customer-payment-methods-section.component.html",
  styleUrl: "./customer-payment-methods-section.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerPaymentMethodsSectionComponent {
  @Input() paymentMethods: PaymentMethod[] = [];
  @Input() readOnly = false;
  @Input() showDelete = true; // Whether to show delete button (old QMC API doesn't support delete)
  @Input() loading = false;

  @Output() deleteMethod = new EventEmitter<number>();
  @Output() addMethod = new EventEmitter<void>();

  /**
   * Get payment method icon based on type
   * Uses PascalCase field names matching API response
   */
  getPaymentIcon(method: PaymentMethod): string {
    if (method.Type === "CreditCard") {
      return "pi pi-credit-card";
    }
    if (method.Type === "PayPal") {
      return "pi pi-paypal";
    }
    if (method.Type === "BankAccount") {
      return "pi pi-building";
    }
    return "pi pi-wallet";
  }

  /**
   * Get payment method display text
   * Uses PascalCase field names matching API response
   */
  getPaymentDisplay(method: PaymentMethod): string {
    if (method.Type === "CreditCard" && method.LastFourDigits) {
      return `Card ending in ${method.LastFourDigits}`;
    }
    return method.Description || method.Type || "Payment Method";
  }

  /**
   * Get expiration display for credit cards
   * Uses PascalCase field names matching API response
   */
  getExpirationDisplay(method: PaymentMethod): string | null {
    if (method.Type === "CreditCard" && method.Expiration) {
      return method.Expiration;
    }
    return null;
  }

  /**
   * Check if card is expired
   * Uses PascalCase field names matching API response
   */
  isExpired(method: PaymentMethod): boolean {
    if (method.Type === "CreditCard" && method.Expiration) {
      // Parse expiration string (format: MM/YY or MM/YYYY)
      const parts = method.Expiration.split("/");
      if (parts.length === 2) {
        const month = parseInt(parts[0], 10);
        let year = parseInt(parts[1], 10);
        // Handle 2-digit year
        if (year < 100) {
          year += 2000;
        }
        const now = new Date();
        const expDate = new Date(year, month, 0);
        return expDate < now;
      }
    }
    return false;
  }

  /**
   * Handle delete click
   * Uses PascalCase field names matching API response
   */
  onDelete(method: PaymentMethod): void {
    this.deleteMethod.emit(method.Id);
  }

  /**
   * Handle add click
   */
  onAdd(): void {
    this.addMethod.emit();
  }

  /**
   * Get all payment methods (API filters out deleted ones)
   */
  getActivePaymentMethods(): PaymentMethod[] {
    return this.paymentMethods;
  }

  /**
   * Check if there are any active payment methods
   */
  hasActivePaymentMethods(): boolean {
    return this.getActivePaymentMethods().length > 0;
  }
}
